import { FC, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Link, useParams } from 'react-router-dom';
import { Badge } from 'reactstrap';
import {
	Button,
	CheckboxController,
	FontAwesome,
	LoadingContent,
	ModalBoxPublishJobTop
} from 'src/containers/Elements';
import { clx } from 'src/helpers/Utils';
import { Colors } from 'src/types/Color';
import { JobKind, JobUsage } from 'src/types/Job';
import styles from './job-partner.module.scss';
import { Subscription, SubscriptionStatus } from 'src/types/Subscription';
import { useTranslation } from 'react-i18next';
import { useUpdateJob } from 'src/hooks/useJob';
import { useDelayUnmount } from 'src/hooks/useDelayUnmount';
import { useModal } from 'src/hooks/useModal';
import { formatLocaleDateTime, getRemainingDay } from 'src/helpers/DateTime';
import axios from 'Adapter';
import { toastify } from 'Utils';
import { api } from 'src/helpers/API';

interface Props {
	subscription: Subscription;
	jobUsageRefetch?: () => void;
	jobUsage: JobUsage[] | undefined;
}

const PartnerCard: FC<Props> = ({
	subscription,
	jobUsage,
	jobUsageRefetch
}) => {
	const { job_id } = useParams();
	const [loading, setLoading] = useState(true);
	const { addTopJob, refetch } = useUpdateJob(job_id as string);
	const { t } = useTranslation();
	const [jobPublishedVisibilities, setJobPublishedVisibilities] =
		useState<any>(null);
	const { watch, getValues, setValue } = useFormContext();
	const [openModal, toggleModal] = useModal();
	const [loadingTopJob, setLoadingTopJob] = useState(false);
	const shouldRenderModal = useDelayUnmount(openModal, 350);
	const { data: jobData } = useUpdateJob(job_id as string);

	useEffect(() => {
		getPublishedJobsDetail();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jobData]);

	const getPublishedJobsDetail = async () => {
		await axios
			.get(api.getPublishedJobsDetail(job_id as string))
			.then((res) => {
				if (res.status === 200) {
					setJobPublishedVisibilities(
						res?.data?.jobPublishedVisibilities
					);
					setValue(
						'jobVisibilities',
						res?.data?.jobPublishedVisibilities,
						{
							shouldDirty: true
						}
					);
				} else {
					setJobPublishedVisibilities([]);
					setValue('jobVisibilities', [], {
						shouldDirty: true
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const defaultImage = '/assets/img/default-image.png';
	const isChecked = watch('jobVisibilities')?.some(
		(v) => v.subscriptionId === subscription.id
	);
	const notActive = subscription.status !== SubscriptionStatus.active;

	const isExpired = subscription.status === SubscriptionStatus.expired;

	const handelSelectPartner = (subscription: Subscription) => {
		const jobVisibilities = getValues('jobVisibilities') || [];

		const setJobVisibilities = jobVisibilities.some(
			(v) => v.subscriptionId === subscription.id
		)
			? jobVisibilities.filter(
					(v) => v.subscriptionId !== subscription.id
			  )
			: [
					...jobVisibilities,
					{
						communityPartnerId: subscription?.communityPartnerInfo
							.communityPartnerId as any,
						subscriptionId: subscription?.id,
						topJob: false,
						topJobExpiredAt: null,
						topJobPublishedAt: null
					}
			  ];
		setValue('jobVisibilities', setJobVisibilities, {
			shouldDirty: true
		});
	};

	const getJobCapacity = useMemo(() => {
		return (kind: JobKind) => {
			if (jobUsage) {
				return jobUsage.find(
					(job) =>
						job.subscriptionId === subscription.id &&
						job.kind === kind
				);
			}
		};
	}, [jobUsage, subscription.id]);

	const jobCapacity = getJobCapacity(JobKind.Job);
	const internshipCapacity = getJobCapacity(JobKind.Internship);

	const hasReachedCapacity = (isChecked: boolean) => {
		const jobKind = getValues('kind');
		if (
			jobKind === JobKind.Job &&
			jobCapacity?.count ===
				Number(
					subscription.subscriptionPlanFeatureInfo?.jobSetting
						?.maxPublishJobs
				) &&
			!isChecked &&
			!jobData?.jobVisibilities?.some(
				(v) => v.subscriptionId === subscription.id
			)
		) {
			return true;
		}
		if (
			jobKind === JobKind.Internship &&
			internshipCapacity?.count ===
				Number(
					subscription.subscriptionPlanFeatureInfo?.internshipSetting
						?.maxPublishInternships
				) &&
			!isChecked &&
			!jobData?.jobVisibilities?.some(
				(v) => v.subscriptionId === subscription.id
			)
		) {
			return true;
		}
	};

	// const hasJobTop = () => {
	// 	if (
	// 		jobData?.jobVisibilities?.some(
	// 			(v) => v.subscriptionId === subscription.id && v.topJob
	// 		)
	// 	) {
	// 		return true;
	// 	}
	// };

	const hasExpiredAt = () => {
		if (
			jobData?.jobVisibilities?.some(
				(v) => v.subscriptionId === subscription.id && v.topJobExpiredAt
			)
		) {
			return true;
		}
	};

	const getExpiredAtJobTop = () => {
		let date = '';
		jobData?.jobVisibilities?.map((item) => {
			if (item?.subscriptionId === subscription.id) {
				date = item?.topJobExpiredAt;
			}
		});
		return date;
	};

	const getPublishedAtJobTop = () => {
		let date = '';
		jobData?.jobVisibilities?.map((item) => {
			if (item?.subscriptionId === subscription.id) {
				date = formatLocaleDateTime(item?.topJobPublishedAt, false);
			}
		});
		return date;
	};

	const handlePublishJobTop = async () => {
		setLoadingTopJob(true);
		await addTopJob(job_id as string, {
			subscriptionId: subscription?.id,
			communityPartnerId: subscription?.communityPartnerInfo
				?.communityPartnerId as any
		}).then(() => {
			toastify('toastify.community-partner-updated', {
				type: 'success'
			});
			refetch();
			jobUsageRefetch && jobUsageRefetch();
			toggleModal();
			setLoadingTopJob(false);
		});
	};

	const topJobCount =
		jobCapacity?.topJobCount || internshipCapacity?.topJobCount || 0;

	return (
		<>
			{loading && <LoadingContent title="loading..." />}
			{!loading && (
				<div
					key={subscription.id}
					className={clx(
						styles['premium-card'],
						isChecked && styles['premium-card--checked'],
						(notActive || hasReachedCapacity(isChecked)) &&
							styles['premium-card--disabled']
					)}>
					<div
						className="d-flex flex-column gap-3"
						onClick={() =>
							!notActive &&
							!hasReachedCapacity(isChecked) &&
							handelSelectPartner(subscription)
						}>
						<div className="d-flex justify-content-center align-items-center gap-2 ">
							<div className="flex-10.5 d-flex justify-content-center align-items-center">
								{!notActive && !hasReachedCapacity(isChecked) && (
									<div>
										<CheckboxController
											isChecked={isChecked}
											name="showDetails"
											id="showDetails"
											label="  "
										/>
									</div>
								)}
								<img
									src={defaultImage}
									alt="Logo"
									className={styles['premium-logo']}
								/>
							</div>

							<div className="flex-1 d-flex  gap-1 flex-column">
								<span className="fw-semibold">
									{
										subscription.communityPartnerInfo
											.organizationName
									}
								</span>
								<span className="fw-light">
									{
										subscription.communityPartnerInfo
											.emailAddress
									}
								</span>
							</div>
						</div>
						<div className="d-flex gap-2">
							<span>
								<FontAwesome
									icon="check"
									className="text-primary"
									size="1x"
								/>
							</span>
							<span>{t('label.your-job-usage')}:</span>
							<span>{`${jobCapacity?.count || 0}/${
								subscription.subscriptionPlanFeatureInfo
									?.jobSetting?.maxPublishJobs == null
									? t('label.unlimited')
									: subscription.subscriptionPlanFeatureInfo
											?.jobSetting?.maxPublishJobs
							}`}</span>
						</div>
						<div className="d-flex gap-2">
							<span>
								<FontAwesome
									icon="check"
									className="text-primary"
									size="1x"
								/>
							</span>
							<span>{t('label.your-internships-usage')}:</span>
							<span>{`${internshipCapacity?.count || 0}/${
								subscription.subscriptionPlanFeatureInfo
									?.internshipSetting
									?.maxPublishInternships === null
									? t('label.unlimited')
									: subscription.subscriptionPlanFeatureInfo
											?.internshipSetting
											.maxPublishInternships
							}`}</span>
						</div>
					</div>
					{subscription.subscriptionPlanFeatureInfo?.jobSetting
						?.topJobEnabled ? (
						<>
							<div className="d-flex gap-2">
								<span>
									<FontAwesome
										icon="check"
										className="text-primary"
										size="1x"
									/>
								</span>
								<span>{t('label.top-list-capacity')}:</span>
								<span>{`${topJobCount}/${
									subscription.subscriptionPlanFeatureInfo
										?.jobSetting?.maxTopJobs === null
										? t('label.unlimited')
										: subscription
												.subscriptionPlanFeatureInfo
												?.jobSetting.maxTopJobs
								}`}</span>
							</div>
							{hasExpiredAt() ? (
								<div className="d-flex flex-column mt-3">
									<div className="d-flex gap-1 fs-sm">
										<span>
											{t(
												'label.published-as-top-list-at'
											)}
										</span>
										<span className="fw-700">
											{getPublishedAtJobTop()}
										</span>
										<span></span>
									</div>
									{new Date() >
									new Date(getExpiredAtJobTop()) ? (
										<div className="d-flex gap-1 fs-sm">
											<span>{t('label.expired-at')}</span>
											<span className="fw-700 text-red">
												{formatLocaleDateTime(
													getExpiredAtJobTop(),
													false
												)}
											</span>
										</div>
									) : (
										<div className="d-flex gap-1 fs-sm">
											<span>{t('label.expired-in')}</span>
											<span className="fw-700">
												{getRemainingDay(
													new Date(),
													new Date(
														getExpiredAtJobTop()
													)
												)}
											</span>
											<span className="fw-700">
												{t('forms.days')}
											</span>
										</div>
									)}
								</div>
							) : jobPublishedVisibilities &&
							  jobPublishedVisibilities?.some(
									(v) => v.subscriptionId === subscription.id
							  ) &&
							  jobData?.publishedVersion ? (
								subscription?.subscriptionPlanFeatureInfo
									?.jobSetting?.maxTopJobs === topJobCount ? (
									<span className="text-warning">
										{t(
											'text.there-is-no-capacity-available-publish-as-top-list'
										)}
									</span>
								) : (
									<div className="d-flex justify-content-end w-100 ">
										<Button
											color={Colors.primary}
											label={
												t(
													'button.boost-this-job-as-top-list'
												) as string
											}
											onClick={toggleModal}
										/>
									</div>
								)
							) : (
								<></>
							)}
						</>
					) : (
						<></>
					)}
					{isExpired && (
						<Badge
							color={Colors.warning}
							className="w-100 d-flex justify-content-between gap-2">
							<div className="w-100 d-flex justify-content-start gap-2">
								<FontAwesome icon="exclamation-circle" />
								<div>Your subscription has been expired</div>
							</div>
							<div className={styles['premium-card--link']}>
								<Link to={''}>Renew Now</Link>
							</div>
						</Badge>
					)}
					{shouldRenderModal && (
						<ModalBoxPublishJobTop
							isOpen={openModal}
							onClose={toggleModal}
							onPublish={handlePublishJobTop}
							isLoading={loadingTopJob}
						/>
					)}
				</div>
			)}
		</>
	);
};

export default PartnerCard;
